import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styles from './PreferenceSection.module.scss';
import Dropdown from '../dropdown/Dropdown';
import Button from '../button/Button';
import Icon from '../icon/Icon';

const PreferenceSection = ({ title, hint, options, items, onChange }) => {
  const [selectedOption, setSelectedOption] = useState(null);

  const availableOptions = options.filter(o => !items.find(i => i.id === o.value));

  const handleAdd = () => {
    if (!selectedOption) return;
    const option = options.find(o => o.value === selectedOption);
    const newItems = [...items, { id: option.value, name: option.label }];
    onChange(newItems);
    setSelectedOption(null);
  };

  const handleRemove = (id) => {
    onChange(items.filter(i => i.id !== id));
  };

  const handleMove = (index, direction) => {
    const newItems = [...items];
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= newItems.length) return;
    [newItems[index], newItems[swapIndex]] = [newItems[swapIndex], newItems[index]];
    onChange(newItems);
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('dragIndex', index);
  };

  const handleDrop = (e, dropIndex) => {
    const dragIndex = parseInt(e.dataTransfer.getData('dragIndex'));
    if (dragIndex === dropIndex) return;
    const newItems = [...items];
    const [dragged] = newItems.splice(dragIndex, 1);
    newItems.splice(dropIndex, 0, dragged);
    onChange(newItems);
  };

  const handleDragOver = (e) => e.preventDefault();

  return (
    <div className={styles.section}>
      <h2 className={styles.title}>{title}</h2>
      <div className={styles.divider} />
      {hint && <p className={styles.hint}>{hint}</p>}

      <div className={styles.addRow}>
        <div className={styles.dropdownWrapper}>
          <Dropdown
            label={title.replace(' Preferences', '')}
            options={availableOptions}
            value={selectedOption}
            onChange={setSelectedOption}
            searchable
          />
        </div>
        <Button label="Add" size="small" onClick={handleAdd} type="button" />
      </div>

      {items.length === 0 ? (
        <div className={styles.empty}>
          No preferences added yet. Select an option above
        </div>
      ) : (
        <div className={styles.list}>
          {items.map((item, index) => (
            <div
              key={item.id}
              className={styles.item}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDrop={(e) => handleDrop(e, index)}
              onDragOver={handleDragOver}
            >
              <span className={styles.dragHandle}>⠿</span>
              <span className={styles.badge}>{index + 1}</span>
              <span className={styles.name}>{item.name}</span>
              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.actionBtn}
                  onClick={() => handleMove(index, -1)}
                  disabled={index === 0}
                >
                  <Icon name="chevron" className={styles.arrowUp} />
                </button>
                <button
                  type="button"
                  className={styles.actionBtn}
                  onClick={() => handleMove(index, 1)}
                  disabled={index === items.length - 1}
                >
                  <Icon name="chevron" className={styles.arrowDown} />
                </button>
                <button
                  type="button"
                  className={styles.removeBtn}
                  onClick={() => handleRemove(item.id)}
                >
                  <Icon name="x-circle" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

PreferenceSection.propTypes = {
  title: PropTypes.string.isRequired,
  hint: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default PreferenceSection;
